type Point = { x: number; y: number };

interface ExtremaPoints {
  top: Point;
  right: Point;
  bottom: Point;
  left: Point;
}

/**
 * 计算以左上角为中心顺时针旋转后的长方形四个极值点（最上/最右/最下/最左）。
 *
 * @param x  左上角顶点的 x
 * @param y  左上角顶点的 y
 * @param w  长方形宽度（>0）
 * @param h  长方形高度（>0）
 * @param a  顺时针角度（度数，支持任意大小与负值）
 */
export const rotatedRectExtrema = (
  x: number,
  y: number,
  w: number,
  h: number,
  a: number
): ExtremaPoints => {
  const EPS = 1e-12;

  // 将角度规整到 [0, 360)
  const rad = ((((a % 360) + 360) % 360) * Math.PI) / 180;

  const snap0 = (n: number) => (Math.abs(n) < EPS ? 0 : n);

  const cos = snap0(Math.cos(rad));
  const sin = snap0(Math.sin(rad));

  // 旋转函数（相对左上角）
  const rotate = (dx: number, dy: number): Point => ({
    x: snap0(x + dx * cos - dy * sin),
    y: snap0(y + dx * sin + dy * cos)
  });

  // 四个顶点
  const A: Point = { x, y }; // 左上角（旋转中心）
  const B = rotate(w, 0); // 右上角
  const C = rotate(w, h); // 右下角
  const D = rotate(0, h); // 左下角
  const pts = [A, B, C, D];

  // 找最小/最大值
  const minY = Math.min(...pts.map((p) => p.y));
  const maxY = Math.max(...pts.map((p) => p.y));
  const minX = Math.min(...pts.map((p) => p.x));
  const maxX = Math.max(...pts.map((p) => p.x));

  // 稳定的选点规则（解决多点并列时的不确定性）
  const pick = (
    candidates: Point[],
    cmp: (a: Point, b: Point) => boolean
  ): Point => candidates.reduce((best, p) => (cmp(p, best) ? p : best));

  const top = pick(
    pts.filter((p) => Math.abs(p.y - minY) <= EPS),
    (a, b) => a.x < b.x
  );
  const bottom = pick(
    pts.filter((p) => Math.abs(p.y - maxY) <= EPS),
    (a, b) => a.x < b.x
  );
  const right = pick(
    pts.filter((p) => Math.abs(p.x - maxX) <= EPS),
    (a, b) => a.y < b.y
  );
  const left = pick(
    pts.filter((p) => Math.abs(p.x - minX) <= EPS),
    (a, b) => a.y < b.y
  );

  return { top, right, bottom, left };
};
